import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Modal, Backdrop, Fade, Typography, Stack, Button, Snackbar, Alert } from "@mui/material";

export interface StageInviteListenerProps {
  channel: string;
  participantId: string | null | undefined;
  onPromoted?: (payload: { rtc?: any; inviteId: string }) => void; // hook to start publishing via Zego
  wsPath?: string; // default "/ws"
}

type InviteEvent = {
  type: "invite.created" | "invite.expired" | "invite.canceled" | "stage.changed";
  inviteId?: string;
  from?: { id: string; name: string };
  ttlSec?: number;
  performerId?: string | null;
  active?: "talent" | "performer";
  // other fields as your server emits
};

const StageInviteListener: React.FC<StageInviteListenerProps> = ({
  channel,
  participantId,
  onPromoted,
  wsPath = "/ws",
}) => {
  const [invite, setInvite] = useState<{ inviteId: string; fromName?: string; ttlSec?: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState<{open: boolean; msg: string; severity: "success"|"error"|"info"}>({
    open: false, msg: "", severity: "info"
  });

  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<number | null>(null);

  const wsUrl = useMemo(() => {
    // Same-origin WS default; adjust if your WS is elsewhere
    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    const qs = new URLSearchParams({
      channel,
      participantId: participantId || "",
    }).toString();
    return `${proto}//${host}${wsPath}?${qs}`;
  }, [channel, participantId, wsPath]);

  useEffect(() => {
    if (!channel || !participantId) return;

    const socket = new WebSocket(wsUrl);
    wsRef.current = socket;

    socket.onopen = () => {
      // optional hello
      socket.send(JSON.stringify({ type: "presence.hello", channel, participantId }));
      // heartbeat
      heartbeatRef.current = window.setInterval(() => {
        try {
          socket.send(JSON.stringify({ type: "presence.heartbeat", participantId }));
        } catch {}
      }, 15000);
    };

    socket.onmessage = (ev) => {
      try {
        const msg: InviteEvent = JSON.parse(ev.data);
        if (msg.type === "invite.created" && msg.inviteId) {
          setInvite({ inviteId: msg.inviteId, fromName: msg.from?.name, ttlSec: msg.ttlSec });
          setOpen(true);
        } else if (msg.type === "invite.expired" || msg.type === "invite.canceled") {
          setOpen(false);
          setInvite(null);
        } else if (msg.type === "stage.changed" && msg.active === "talent") {
          // Performance ended → close any invite UI
          setOpen(false);
          setInvite(null);
        }
      } catch {}
    };

    socket.onerror = () => {
      setToast({ open: true, msg: "Realtime connection error.", severity: "error" });
    };

    socket.onclose = () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
    };

    return () => {
      if (heartbeatRef.current) {
        clearInterval(heartbeatRef.current);
        heartbeatRef.current = null;
      }
      socket.close();
      wsRef.current = null;
    };
  }, [channel, participantId, wsUrl]);

  const acceptInvite = async () => {
    if (!invite?.inviteId) return;
    try {
      const res = await fetch(`/v1/channels/${channel}/performer-invites/${invite.inviteId}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participantId }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to accept invite");
      }
      const data = await res.json().catch(() => ({}));
      setToast({ open: true, msg: "You're on stage! 🎤", severity: "success" });
      setOpen(false);
      setInvite(null);
      onPromoted?.({ rtc: data?.rtc, inviteId: invite.inviteId });
      // Here your parent can start Zego publishing using returned rtc
    } catch (e: any) {
      setToast({ open: true, msg: e?.message || "Could not accept invite", severity: "error" });
    }
  };

  const declineInvite = async () => {
    if (!invite?.inviteId) {
      setOpen(false);
      return;
    }
    try {
      await fetch(`/v1/channels/${channel}/performer-invites/${invite.inviteId}/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ participantId }),
      });
    } catch {}
    setOpen(false);
    setInvite(null);
  };

  return (
    <>
      <Modal
        open={open}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300, sx: { backgroundColor: "rgba(0,0,0,0.6)" } } }}
      >
        <Fade in={open}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "#111",
              color: "#fff",
              px: 5,
              py: 4,
              borderRadius: 3,
              width: { xs: "90vw", sm: 420 },
              textAlign: "center",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <Typography variant="h6" sx={{ fontFamily: "Lexend", fontWeight: 700, mb: 1 }}>
              You’re invited to perform
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)", mb: 3 }}>
              {invite?.fromName ? `${invite.fromName} is inviting you on stage.` : "The DJ invited you on stage."}
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center">
              <Button
                onClick={acceptInvite}
                variant="contained"
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend",
                  fontWeight: 600,
                  backgroundColor: "#22c55e",
                  "&:hover": { backgroundColor: "#16a34a" },
                }}
              >
                Accept
              </Button>
              <Button
                onClick={declineInvite}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  fontFamily: "Lexend",
                  borderColor: "rgba(255,255,255,0.3)",
                  color: "#fff",
                  "&:hover": { borderColor: "#fff", background: "rgba(255,255,255,0.08)" },
                }}
              >
                Decline
              </Button>
            </Stack>
          </Box>
        </Fade>
      </Modal>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast(prev => ({ ...prev, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StageInviteListener;
